/**
 * Demo-mode network firewall for apiClient.
 *
 * While `auth.isDemo` is true, EVERY request is intercepted BEFORE any auth
 * header is attached and BEFORE it can reach the relay (see api/client.ts).
 * A request is either:
 *   1. Resolved from a bundled, in-memory fixture (for the handful of pages
 *      that fetch-on-mount), returning the standard SSP envelope shape, or
 *   2. Hard-rejected with a `DemoModeError`.
 *
 * In NEITHER case does the request leave the browser, and NO Bearer token is
 * ever attached (demo never mints a sessionToken). This module imports nothing
 * from the signing/wallet layer — see DEMO_TREASURY_DESIGN.md §3.3 / §8.
 */

import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Thrown by the apiClient request interceptor for any demo-mode request that
 * has no fixture adapter. The UI recognises this sentinel and shows a friendly
 * "you're in the demo" nudge instead of a raw network error.
 */
export class DemoModeError extends Error {
  readonly isDemoModeError = true;
  readonly url?: string;

  constructor(url?: string) {
    super('This action is unavailable in the live demo.');
    this.name = 'DemoModeError';
    this.url = url;
  }
}

export function isDemoModeError(err: unknown): err is DemoModeError {
  return (
    err instanceof DemoModeError ||
    (typeof err === 'object' &&
      err !== null &&
      (err as { isDemoModeError?: boolean }).isDemoModeError === true)
  );
}

/**
 * A fixture adapter resolves a GET request to fixture data (already wrapped in
 * the SSP `{ status: 'success', data }` envelope as `data`). Return `null` to
 * fall through to a hard reject. Adapters are pure functions of the request
 * config — they never touch the network.
 */
export type DemoFixtureAdapter = (
  config: InternalAxiosRequestConfig
) => Record<string, unknown> | null;

let fixtureAdapter: DemoFixtureAdapter | null = null;

/**
 * Register the bundled fixture adapter. Called once by seedDemoStore() at demo
 * entry. Kept out of client.ts so the (heavy) fixtures stay out of the network
 * layer's import graph until a demo is actually entered.
 */
export function registerDemoFixtureAdapter(adapter: DemoFixtureAdapter): void {
  fixtureAdapter = adapter;
}

export function clearDemoFixtureAdapter(): void {
  fixtureAdapter = null;
}

/**
 * Resolve a demo request. Returns a synthetic AxiosResponse when a fixture
 * adapter matches a safe (GET) request, otherwise throws DemoModeError.
 *
 * Only GET requests are ever served from fixtures — any mutating verb
 * (POST/PUT/PATCH/DELETE) is always hard-rejected so the demo can never appear
 * to "write".
 */
export function resolveDemoRequest(config: InternalAxiosRequestConfig): AxiosResponse {
  const method = (config.method ?? 'get').toLowerCase();
  if (method === 'get' && fixtureAdapter) {
    const data = fixtureAdapter(config);
    if (data) {
      return {
        data,
        status: 200,
        statusText: 'OK (demo fixture)',
        headers: {},
        config,
      } as AxiosResponse;
    }
  }
  throw new DemoModeError(config.url);
}
